import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { GroupType } from "../../lib/types";
import GroupTypeSelector from "./GroupTypeSelector";

interface CreateGroupFormProps {
  onSubmit: (payload: {
    name: string;
    type: GroupType;
    description?: string;
  }) => Promise<void> | void;
  onCancel?: () => void;
  loading?: boolean;
}

const CreateGroupForm: React.FC<CreateGroupFormProps> = ({
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<GroupType>("family");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      return;
    }
    console.log("Submitting new group", { name: name.trim(), type });
    await onSubmit({
      name: name.trim(),
      type,
      description: description.trim() || undefined,
    });
  };

  return (
    <Card elevation={0} sx={{ borderRadius: 1 }}>
      <CardContent>
        <Stack spacing={3} component="form" onSubmit={handleSubmit}>
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Create a new group
            </Typography>
            <Typography color="text.secondary">
              Keep it personal or build a business workspace.
            </Typography>
          </Box>

          <TextField
            label="Group name"
            placeholder="e.g. House Budget"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Description"
            placeholder="Short summary for teammates"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />

          <GroupTypeSelector value={type} onChange={setType} />

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title="Create group">
              <span>
                <IconButton type="submit" disabled={!name.trim() || loading}>
                  {loading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <CheckCircleIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            {onCancel && (
              <Tooltip title="Cancel">
                <span>
                  <IconButton
                    onClick={() => {
                      console.log("Canceling create group");
                      onCancel();
                    }}
                    disabled={loading}
                  >
                    <CloseIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CreateGroupForm;
